'use client'

import Link from 'next/link'
import { useParams } from 'next/navigation'
import { Fragment } from 'react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/v2/ui/breadcrumb'
import Icon, { type IAvailableIcons } from '@/components/v2/ui/icon'
import { useTexts } from '@/contexts/LanguageContext'
import { trustCenterPath } from '../utils'

interface IBreadcrumbLink {
  href: string
  label: string
}

interface IPageHeaderProps {
  title: string
  subtitle?: string
  parents?: IBreadcrumbLink[]
  icon?: IAvailableIcons
  action?: React.ReactNode
}

function PageBreadcrumb({ title, parents = [] }: { title: string; parents?: IBreadcrumbLink[] }) {
  const { PUBLIC_TRUST_CENTER } = useTexts()
  const { orgToken } = useParams<{ orgToken: string }>()
  const links = [{ href: trustCenterPath(orgToken), label: PUBLIC_TRUST_CENTER.TITLE }, ...parents]
  return (
    <Breadcrumb>
      <BreadcrumbList className="text-trust-center-muted">
        {links.map((link) => (
          <Fragment key={link.href}>
            <BreadcrumbItem>
              <BreadcrumbLink asChild className="text-trust-center-accent hover:text-trust-center">
                <Link href={link.href}>{link.label}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </Fragment>
        ))}
        <BreadcrumbItem>
          <BreadcrumbPage className="font-medium text-trust-center">{title}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export default function PageHeader({ title, subtitle, parents, icon, action }: IPageHeaderProps) {
  return (
    <div className="flex flex-col gap-6">
      <PageBreadcrumb title={title} parents={parents} />
      <div className="flex items-start gap-4">
        {icon && (
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-trust-center-subtle">
            <Icon icon={icon} className="size-5" />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-trust-center-muted">{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  )
}
